import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { describeMissingOwnClaimField } from "../OwnClaimsView.missingFields";
import { AssetOwnClaimsFormValues } from "../OwnClaimsView.types";
import { fromOwnClaimsResponse, toOwnClaimsRequest } from "../OwnClaimsView.utils";
import { createOwnClaimsSchema } from "../OwnClaimsView.validation";

export function useAssetOwnClaims(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);

  const queryKey = [ClientFileQueryKey.AssetInventoryOwnClaims, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryOwnClaims({ clientId }),
    enabled: !!clientId,
  });

  const uploadDocuments = async (files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryOwnClaimDocument(
            { clientId },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryOwnClaimDocuments, clientId] });
    return failed;
  };

  const validationSchema = useMemo(() => createOwnClaimsSchema(t), [t]);

  const initialValues = useMemo(() => fromOwnClaimsResponse(data), [data]);

  const formik = useFormikWithDirtyForm<AssetOwnClaimsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const request = toOwnClaimsRequest(values);

      if (JSON.stringify(request) !== JSON.stringify(toOwnClaimsRequest(initialValues))) {
        await clientsApi.saveAssetInventoryOwnClaims({ clientId, saveAssetOwnClaimsRequest: request });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
        await queryClient.invalidateQueries({ queryKey });
      }

      if (values.hasOwnClaims !== true) {
        await helpers.setFieldValue(nameof<AssetOwnClaimsFormValues>("ownClaimDocuments"), []);
        await helpers.setFieldValue(nameof<AssetOwnClaimsFormValues>("documentMoves"), []);
        showSuccess(t("clients.client.debtSettlement.assetInventory.ownClaimsTab.saveSuccess"));
        return;
      }

      let uploadFailed = false;
      if (values.ownClaimDocuments.length > 0) {
        const failed = await uploadDocuments(values.ownClaimDocuments);
        await helpers.setFieldValue(nameof<AssetOwnClaimsFormValues>("ownClaimDocuments"), failed);
        uploadFailed = failed.length > 0;
      }

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue(nameof<AssetOwnClaimsFormValues>("documentMoves"), failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.ownClaimsTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.OwnClaims,
    formik.values,
    describeMissingOwnClaimField
  );

  return { formik, missingFields, onAddMove, onCancelMove };
}
